import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ─── Thunks ───

export const fetchFavoriteListings = createAsyncThunk(
  'FavoritesPage/fetchListings',
  (favoriteIds, { dispatch, rejectWithValue, extra: sdk }) => {
    if (!favoriteIds || favoriteIds.length === 0) {
      return { data: [] };
    }

    // `listing-card` is a CUSTOM variant — requesting it via
    // fields.image alone returns image entities with NO attributes
    // (found live 2026-07-02: every favorites card said "NO IMAGE").
    // The variant definitions must ride along, exactly as the search
    // page's query builds them.
    return sdk.listings
      .query({
        ids: favoriteIds,
        include: ['images'],
        'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
        ...createImageVariantConfig('listing-card', 400, 1),
        ...createImageVariantConfig('listing-card-2x', 800, 1),
        'limit.images': 1,
      })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response.data;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

// ─── Slice ───

const favoritesPageSlice = createSlice({
  name: 'FavoritesPage',
  initialState: {
    listingRefs: [],
    fetchInProgress: false,
    fetchError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFavoriteListings.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchFavoriteListings.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.listingRefs = (action.payload?.data || []).map(l => ({
          id: l.id,
          type: 'listing',
        }));
      })
      .addCase(fetchFavoriteListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default favoritesPageSlice.reducer;

// loadData is not used for this page since favorites come from currentUser
// which is already loaded. We fetch listings client-side in useEffect.
export const loadData = () => () => Promise.resolve();
